/**
 * React context for data that is used repeatedly throughout the application.
 * This prevents needing to make repeated queries to get those details in multiple components, and prevents needing to prop drill throughout the component tree.
 * In this context (pun unintended), Client refers to a SummIT customer.
 */

import { type JSX, createContext, useCallback, useContext, useEffect, useState } from 'react';

import { ClientDetailsApi } from 'Api/Client/ClientDetailsApi';
import { ComplianceRequirementsApi } from 'Api/ComplianceRequirements/ComplianceRequirementsApi';
import { TagsApi } from 'Api/Tags/TagsApi';
import { UsersApi } from 'Api/Users/UsersApi';
import { Role, userHasAuthorizedRole } from 'Components/Context/RBACContext';
import { Placeholder } from 'Components/Placeholder/Placeholder';
import { Text } from 'Components/Text/Text';
import { CONTROL_AUTOMATION_ROLES } from 'Helpers/UserUtils';
import { AdvancedFeature, ClientDetailsModel } from 'Models/ClientDetailsModel';
import { Regulation, StatusCategory, StatusDefinition, resolveStatusDefinition } from 'Models/ComplianceRequirements';
import { DisplayableTag, TagCategory } from 'Models/Tags';
import { GroupedOptions, OptionType } from 'Models/Types/GlobalType';
import { UserResponse } from 'Models/User';

export type AdvancedFeatureAvailability =
    | {
          isAvailable: true;
      }
    | {
          isAvailable: false;
          feature: AdvancedFeature;
          reason: 'notGranted' | 'grantedButDisabled';
      };

// TODO: Consider memoizing an entire `UserResponse` object for the logged-in user, dependent on all users being loaded and fetching the user's access token from local storage.
/**
 * @param checkAdvancedFeatureAvailability A function that performs more in-depth calculations than `clientHasAdvancedFeature`. Use this in conjunction with the `AdvancedFeatureUnavailableExplanation` component when you want to describe a feature the Client does not have access to and to explain why they don't have access (i.e., do they not have the feature or do they have it and have disabled it?).
 * @param clientDetails The Client's (customer's) information, including the advanced features.
 * @param clientHasAdvancedFeature A function that compares the Client's advanced features against a list of authorized features to determine feature access. Use this when you only need a simple "yes/no" on whether the Client can access something.
 * @param convertTagIdsToDisplayableTags A function that takes an array of Tag IDs and returns an array of DisplayableTag objects. This is used to convert Tag IDs to their corresponding metadata for display purposes.
 * @param getComplianceAggregationMode Returns the aggregation_mode string for a Regulation, or undefined if the Regulation is not found.
 * @param getComplianceMaturityStatusOptions Returns ordered maturity-level options for a Regulation that uses the FFIEC CAT maturity aggregation mode. Used for target maturity selectors.
 * @param getComplianceStatusOptions Returns the selectable status options for a Regulation in display order. Used to populate status dropdowns in Control Assessments.
 * @param getComplianceStatusDefinition Returns the StatusDefinition for a given status value within a Regulation, or undefined if not found.
 * @param getTagCategoriesGroupedOptions A function that returns a list of Tag Categories and their Tags in a format suitable for use in drop-down menus.
 * @param getTagsMap A function that returns a map of Tag IDs to Tag metadata. This allows various components to look up Tag details by ID.
 * @param refreshClientDetails A function that updates cached Client details. Client details are updated in only one place in the app that returns an updated object; this function takes that object as an argument rather than redundantly fetching from the backend.
 * @param refreshTags A function that refreshes the Tag Categories and Tags from the API. This is used to update the Tag hierarchy when it changes.
 * @param refreshUsers A function that refreshes the list of Users from the API. This is used to update the list of Users when it changes.
 * @param regulations The list of Regulations available to this Client, including each Regulation's available statuses and aggregation mode.
 * @param tagCategories Stores the "raw" Tag hierarchy from the API. Different parts of the application need to use Tags in different formats, so we cache them in this format and have other functions convert them to other formats.
 * @param users Stores the list of Users from the API.
 */
interface CachedDataContextValue {
    checkAdvancedFeatureAvailability: (feature: AdvancedFeature) => AdvancedFeatureAvailability;
    clientDetails: ClientDetailsModel;
    clientHasAdvancedFeature: (features: AdvancedFeature[]) => boolean;
    convertTagIdsToDisplayableTags: (tagIds: string[]) => DisplayableTag[];
    getComplianceAggregationMode: (regulationName: string) => string | undefined;
    getComplianceMaturityStatusOptions: (regulationName: string) => OptionType[];
    getComplianceStatusOptions: (regulationName: string) => OptionType[];
    getComplianceStatusDefinition: (regulationName: string, value: string | null | undefined) => StatusDefinition | undefined;
    getTagCategoriesGroupedOptions: () => GroupedOptions[];
    getTagsMap: () => Map<string, DisplayableTag>;
    refreshClientDetails: (details: ClientDetailsModel) => void;
    refreshTags: () => void;
    refreshUsers: () => void;
    regulations: Regulation[];
    tagCategories: TagCategory[];
    users: UserResponse[];
}
export const CachedDataContext = createContext<CachedDataContextValue | undefined>(undefined);

interface CachedDataProviderProps {
    children: React.ReactNode;
    clientDetailsApi: ClientDetailsApi;
    complianceRequirementsApi: ComplianceRequirementsApi;
    tagsApi: TagsApi;
    userRoles: Role[];
    usersApi: UsersApi;
}
export const CachedDataProvider = (props: CachedDataProviderProps): JSX.Element => {
    const [clientDetails, setClientDetails] = useState<ClientDetailsModel>();
    const [error, setError] = useState<Error>();
    const [regulations, setRegulations] = useState<Regulation[]>();
    const [tagCategories, setTagCategories] = useState<TagCategory[]>();
    const [users, setUsers] = useState<UserResponse[]>();

    /** Get details for the Client from the API. */
    useEffect(() => {
        const getClientDetails = async (): Promise<void> => {
            try {
                const clientDetailsResponse = await props.clientDetailsApi.getClientDetails();
                setClientDetails(clientDetailsResponse.data);
            } catch (error) {
                setError(error);
            }
        };
        getClientDetails();
    }, [props.clientDetailsApi]);

    /** Get the list of Regulations (with their available statuses and aggregation mode) from the API. */
    const getRegulations = useCallback(() => {
        const getAvailableRegulations = async (): Promise<void> => {
            if (!userHasAuthorizedRole(props.userRoles, CONTROL_AUTOMATION_ROLES)) {
                setRegulations([]);
                return;
            }

            try {
                const response = await props.complianceRequirementsApi.getAvailableRegulations();
                setRegulations(response.data);
            } catch (error) {
                setError(error);
            }
        };
        getAvailableRegulations();
    }, [props.complianceRequirementsApi, props.userRoles]);

    /** Get the Client's hierarchy of sorted Tag Categories and Tags from the API. */
    const getTags = useCallback(() => {
        const getTags = async (): Promise<void> => {
            try {
                const response = await props.tagsApi.getTags();
                const tagCategories = response.data.category_set;
                tagCategories.sort((a, b) => (a.title > b.title ? 1 : -1));
                tagCategories.forEach((tagCategory) => tagCategory.tags.sort((a, b) => (a.title > b.title ? 1 : -1)));
                setTagCategories(tagCategories);
            } catch (error) {
                setError(error);
            }
        };
        getTags();
    }, [props.tagsApi]);

    /** Get a list of Users from the API. */
    const getUsers = useCallback(() => {
        const getUsers = async (): Promise<void> => {
            try {
                const usersResponse = await props.usersApi.getUsers();
                const users = usersResponse.data;
                setUsers(users);
            } catch (error) {
                setError(error);
            }
        };
        getUsers();
    }, [props.usersApi]);

    useEffect(() => {
        getRegulations();
        getTags();
        getUsers();
    }, [getRegulations, getTags, getUsers]);

    if (error) {
        return <Text>{error.message}</Text>;
    } else if (clientDetails === undefined || regulations === undefined || tagCategories === undefined || users === undefined) {
        return <Placeholder />;
    } else {
        /**************
         * Client *
         *************/

        /** Compare the Client's advanced features against a single advanced aeature, returning details about why the feature is unavailable. */
        const checkAdvancedFeatureAvailability = (feature: AdvancedFeature): AdvancedFeatureAvailability => {
            const isPresent = feature in clientDetails.advanced_features;
            const isEnabled = clientDetails.advanced_features[feature] === true;

            if (isPresent) {
                if (isEnabled) {
                    return { isAvailable: true };
                } else {
                    return { isAvailable: false, feature, reason: 'grantedButDisabled' };
                }
            } else {
                return { isAvailable: false, feature, reason: 'notGranted' };
            }
        };

        /** Compare the Client's advanced features to authorized features to determine if at least one feature is both granted (purchased) and enabled. */
        const clientHasAdvancedFeature = (features: AdvancedFeature[]): boolean => {
            return features.some((feature) => clientDetails.advanced_features[feature] === true);
        };

        /**************
         * Compliance *
         *************/

        /** Build a map of Regulation names to Regulation objects for O(1) lookup. */
        const regulationsMap = new Map<string, Regulation>(regulations.map((regulation) => [regulation.name, regulation]));

        /** Returns the aggregation_mode of a Regulation by name, or undefined if not found. */
        const getComplianceAggregationMode = (regulationName: string): string | undefined => regulationsMap.get(regulationName)?.aggregation_mode;

        /** Returns ordered maturity-level options (e.g. Baseline through Innovative) for a Regulation, suitable for target maturity selectors. */
        const getComplianceMaturityStatusOptions = (regulationName: string): OptionType[] => {
            const regulation = regulationsMap.get(regulationName);
            if (!regulation) {
                return [];
            }
            return regulation.available_statuses
                .filter((s) => s.category === StatusCategory.MATURITY)
                .sort((a, b) => a.order - b.order)
                .map((s) => ({ label: s.value, value: s.value }));
        };

        /** Returns the StatusDefinition for a status value within a Regulation, or undefined if not found. */
        const getComplianceStatusDefinition = (regulationName: string, value: string | null | undefined): StatusDefinition | undefined => {
            const regulation = regulationsMap.get(regulationName);
            return regulation ? resolveStatusDefinition(regulation.available_statuses, value) : undefined;
        };

        /** Returns selectable status options for a Regulation, sorted by each status's display order. */
        const getComplianceStatusOptions = (regulationName: string): OptionType[] => {
            const regulation = regulationsMap.get(regulationName);
            return regulation
                ? regulation.available_statuses
                      .filter((s) => s.selectable)
                      .sort((a, b) => a.order - b.order)
                      .map((s) => ({ label: s.value, value: s.value }))
                : [];
        };

        /********
         * Tags *
         *******/

        /** Given an array of Tag IDs, reference them against the Tag Category hierarchy in state to return a corresponding array of Tag metadata. */
        const convertTagIdsToDisplayableTags = (tagIds: string[]): DisplayableTag[] => {
            const tagsMap = getTagsMap();
            return tagIds.map((tagId) => tagsMap.get(tagId)!);
        };

        /** Returns a list of Tags converted to GroupedOptions. This allows Tags to be displayed in drop-down menus. */
        const getTagCategoriesGroupedOptions = (): GroupedOptions[] => {
            const tagCategoriesGroupedOptions = tagCategories.map((tagCategory) => ({
                label: tagCategory.title,
                options: tagCategory.tags.map((tag) => ({
                    groupId: tagCategory.title,
                    label: tag.title,
                    value: tag.id!, // TODO: `!` should not be needed.
                })),
            }));
            return tagCategoriesGroupedOptions;
        };

        /** Returns a map of Tag IDs to Tag metadata. This allows various components to look up Tag details by ID. */
        const getTagsMap = (): Map<string, DisplayableTag> => {
            const tagsMap = new Map<string, DisplayableTag>();
            tagCategories.forEach((tagCategory) => {
                tagCategory.tags.forEach((tag) => {
                    tagsMap.set(tag.id!, { id: tag.id!, title: tag.title, category_color: tagCategory.color, category_title: tagCategory.title });
                });
            });
            return tagsMap;
        };

        return <CachedDataContext.Provider value={{ clientDetails: clientDetails, checkAdvancedFeatureAvailability: checkAdvancedFeatureAvailability, clientHasAdvancedFeature: clientHasAdvancedFeature, convertTagIdsToDisplayableTags: convertTagIdsToDisplayableTags, getComplianceAggregationMode: getComplianceAggregationMode, getComplianceMaturityStatusOptions: getComplianceMaturityStatusOptions, getComplianceStatusOptions: getComplianceStatusOptions, getComplianceStatusDefinition: getComplianceStatusDefinition, getTagsMap: getTagsMap, getTagCategoriesGroupedOptions: getTagCategoriesGroupedOptions, refreshClientDetails: setClientDetails, refreshTags: getTags, refreshUsers: getUsers, regulations: regulations, tagCategories: tagCategories, users: users }}>{props.children}</CachedDataContext.Provider>;
    }
};

export const useCachedData = (): CachedDataContextValue => {
    const context = useContext(CachedDataContext);
    if (context === undefined) {
        throw new Error('useCachedData must be used within a CachedDataProvider.');
    }
    return context;
};
